import React from "react";
import { LucideIcon } from "lucide-react";
import { getContrastingColor } from "../../lib/utils";

// Color utility to extract color value from Tailwind class or simple color name
const getColorValue = (colorClass: string): string => {
  const colorMap: Record<string, string> = {
    // Full Tailwind classes
    'bg-primary': '280 100% 70%', // Vibrant purple for Gia's prominence
    'bg-blue-500': '220 95% 62%',
    'bg-green-500': '142 71% 45%',
    'bg-purple-500': '258 90% 66%',
    'bg-indigo-500': '239 84% 67%',
    'bg-cyan-500': '188 94% 63%',
    'bg-amber-500': '43 96% 56%',
    'bg-teal-500': '173 80% 40%',
    'bg-pink-500': '330 81% 60%',
    'bg-orange-500': '25 95% 53%',
    'bg-red-500': '0 84% 60%',
    
    // Simple color names for About nodes
    'purple': '258 90% 66%',
    'blue': '220 95% 62%',
    'green': '142 71% 45%',
    'indigo': '239 84% 67%',
    'cyan': '188 94% 63%',
    'amber': '43 96% 56%',
    'teal': '173 80% 40%',
    'pink': '330 81% 60%',
    'orange': '25 95% 53%',
    'red': '0 84% 60%'
  };
  return colorMap[colorClass] || '280 100% 70%';
};

interface NeuralNodeCoreProps {
  agent: {
    icon: LucideIcon;
    title: string;
    color: string;
  };
  size: 'central' | 'large' | 'medium' | 'small';
  sizeClasses: Record<string, string>;
  iconSizes: Record<string, string>;
  isInteractive: boolean;
  isActive: boolean;
  isMobile: boolean;
}

export const NeuralNodeCore: React.FC<NeuralNodeCoreProps> = ({
  agent,
  size,
  sizeClasses,
  iconSizes,
  isInteractive,
  isActive,
  isMobile
}) => {
  const agentColorValue = getColorValue(agent.color);
  const isGia = agent.title === 'Gia';
  const iconColor = getContrastingColor(agentColorValue);
  
  return (
    <>
      {/* Neural Glow */}
      <div 
        className={`
          ${sizeClasses[size]} absolute inset-0 rounded-full blur-xl transition-all duration-700
          ${isInteractive ? 'opacity-90 scale-150' : 'opacity-70 scale-100'}
        `}
        style={{
          background: isGia 
            ? `radial-gradient(circle, hsl(${agentColorValue} / 0.4), hsl(var(--accent) / 0.3), hsl(${agentColorValue} / 0.2))`
            : `radial-gradient(circle, hsl(${agentColorValue} / 0.4), hsl(${agentColorValue} / 0.2), transparent)`
        }}
      />
      
      {/* Pulsing Rings */}
      {!window.matchMedia('(prefers-reduced-motion: reduce)').matches && (
        <>
          <div className={`${sizeClasses[size]} absolute inset-0 rounded-full animate-ping`}>
            <div 
              className="w-full h-full rounded-full" 
              style={{ backgroundColor: `hsl(${agentColorValue} / 0.2)` }}
            />
          </div>
          <div 
            className={`${sizeClasses[size]} absolute inset-0 rounded-full animate-pulse`}
            style={{ animationDelay: '1s' }}
          >
            <div 
              className="w-full h-full rounded-full" 
              style={{ backgroundColor: `hsl(${agentColorValue} / 0.15)` }}
            />
          </div>
        </>
      )}
      
      {/* Main Node */}
      <div
        className={`
          ${sizeClasses[size]} relative rounded-full border-2 transition-all duration-500 z-10
          ${isInteractive ? 'scale-110' : 'scale-100'}
          flex items-center justify-center
        `}
        style={{
          borderColor: isInteractive 
            ? `hsl(${agentColorValue} / 0.6)` 
            : `hsl(${agentColorValue} / 0.3)`,
          backgroundColor: `hsl(${agentColorValue} / ${isInteractive ? '0.9' : '0.8'})`,
          boxShadow: isInteractive 
            ? `0 0 40px hsl(${agentColorValue} / 0.4), 0 20px 40px -10px hsl(${agentColorValue} / 0.3), inset 0 1px 0 hsl(${agentColorValue} / 0.2)` 
            : `0 0 20px hsl(${agentColorValue} / 0.2), 0 10px 20px -5px hsl(${agentColorValue} / 0.2), inset 0 1px 0 hsl(${agentColorValue} / 0.1)`
        }}
      >
        {/* Neural Activity Indicator */}
        <div className="absolute inset-0 rounded-full overflow-hidden">
          <div 
            className={`
              absolute inset-0 transform -skew-x-12 transition-all duration-1000
              ${isInteractive && !window.matchMedia('(prefers-reduced-motion: reduce)').matches 
                ? 'animate-neural-fire' 
                : 'translate-x-[-200%]'}
            `}
            style={{
              background: `linear-gradient(to right, transparent, hsl(${agentColorValue} / 0.3), transparent)`
            }}
          />
        </div>
        
        {/* Icon */}
        <agent.icon 
          className={`
            ${iconSizes[size]} transition-all duration-300 relative z-10
            ${isInteractive ? 'rotate-12' : ''}
          `}
          style={{
            color: '#ffffff',
            opacity: isInteractive ? 1 : 0.9,
            transform: agent.title === 'Scribe' ? 'translateY(-15%)' : undefined,
            textShadow: '0 0 8px rgba(0,0,0,0.8), 0 0 16px rgba(0,0,0,0.4)'
          }}
        />
        
        {/* Mobile touch indicator */}
        {isMobile && (
          <div 
            className="absolute -bottom-1 -right-1 w-2 h-2 rounded-full transition-all duration-300"
            style={{
              backgroundColor: agent.color,
              opacity: isActive ? 1 : 0.6,
            }}
          />
        )}
      </div>
    </>
  );
};